import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type {
  CreateResidentTodoInput,
  DeleteResidentTodoInput,
  RepeatRule,
  ResidentTodoOccurrenceRecord,
  ResidentTodoRecord,
  UpdateResidentTodoInput,
} from '@/types/residents/todos';

export type { CreateResidentTodoInput, DeleteResidentTodoInput, UpdateResidentTodoInput };

/**
 * RTK Query slice for resident personal todos (spec 2026-08-11; repeats:
 * spec 2026-10-02 §7.1). Mirrors todoListsApi's conventions: `createApi` +
 * `baseQueryWithReauth`, url style `api/resident-todos...` (no leading slash),
 * `{ success, data }` envelope unwrapped in `transformResponse`, per-id tags +
 * a LIST sentinel — never the bare 'ResidentTodo' string.
 *
 * ONE arg-less query holds the stored records (one-offs and repeats with
 * their per-date records); surfaces expand dates for their own range
 * (todoRecurrence), so paging never refetches. The query MUST stay arg-less:
 * the optimistic patches below target cache key `undefined`.
 */

const LIST_TAG_ID = 'LIST' as const;

/** Backend success envelope for endpoints that return a payload. */
interface DataEnvelope<T> {
  success: boolean;
  data: T;
}

/** Server row shape for api/resident-todos. */
interface ResidentTodoRow {
  id: number;
  accountId: number;
  title: string;
  dueDate: string;
  completed: boolean;
  notes: string | null;
  /** Absent on a backend that predates times — treated as untimed. */
  dueTime?: string | null;
  /** Absent on a backend that predates repeats — treated as a one-off. */
  repeat?: RepeatRule | null;
  /** A backend that predates times sends no `time`. */
  occurrences?: Array<Omit<ResidentTodoOccurrenceRecord, 'time'> & { time?: string | null }>;
}

const toClientRecord = (row: ResidentTodoRow): ResidentTodoRecord => ({
  id: String(row.id),
  title: row.title,
  dueDate: row.dueDate,
  dueTime: row.dueTime ?? null,
  completed: row.completed,
  notes: row.notes ?? undefined,
  repeat: row.repeat ?? null,
  occurrences: (row.occurrences ?? []).map(o => ({ ...o, time: o.time ?? null })),
});

const DEFAULT_OCCURRENCE = {
  completed: false,
  cancelled: false,
  movedTo: null,
  time: null,
  title: null,
  notes: null,
};

/**
 * A date record with nothing left to say: not ticked, not cancelled, not moved, no time of its own
 * (a removed time, '', IS one), not retitled, and its notes not overridden ('' — cleared for this
 * date — IS an override). The server deletes such a record (planSingle), so the optimistic cache
 * drops it too and matches what the refetch returns.
 */
const isDefaultOccurrence = (o: ResidentTodoOccurrenceRecord): boolean =>
  !o.completed &&
  !o.cancelled &&
  o.movedTo == null &&
  o.time == null &&
  o.title == null &&
  o.notes == null;

export const residentTodosApi = createApi({
  reducerPath: 'residentTodosApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ResidentTodo'],
  endpoints: builder => ({
    // GET api/resident-todos — all of the caller's todos, date-ascending.
    getResidentTodos: builder.query<ResidentTodoRecord[], void>({
      query: () => 'api/resident-todos',
      transformResponse: (response: DataEnvelope<ResidentTodoRow[]>) =>
        response.data.map(toClientRecord),
      providesTags: result =>
        result
          ? [
              ...result.map(todo => ({ type: 'ResidentTodo' as const, id: todo.id })),
              { type: 'ResidentTodo' as const, id: LIST_TAG_ID },
            ]
          : [{ type: 'ResidentTodo' as const, id: LIST_TAG_ID }],
    }),

    // POST api/resident-todos
    addResidentTodo: builder.mutation<ResidentTodoRecord, CreateResidentTodoInput>({
      query: body => ({ url: 'api/resident-todos', method: 'POST', body }),
      transformResponse: (response: DataEnvelope<ResidentTodoRow>) =>
        toClientRecord(response.data),
      invalidatesTags: [{ type: 'ResidentTodo', id: LIST_TAG_ID }],
    }),

    // PATCH api/resident-todos/:id — the edit form, the one-off checkbox, and
    // (scope 'single') one date of a repeat. Optimistic only where the result
    // is certain: an unscoped patch to a ONE-OFF, and a single-date tick (an
    // un-tick that leaves the date's record all-default drops it, as the server
    // does). Scoped edits, conversions and an unscoped patch that lands on a
    // repeat wait for the server (the form already waits); a failed request
    // visibly rolls back.
    updateResidentTodo: builder.mutation<ResidentTodoRecord, UpdateResidentTodoInput>({
      query: ({ id, ...body }) => ({
        url: `api/resident-todos/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: DataEnvelope<ResidentTodoRow>) =>
        toClientRecord(response.data),
      async onQueryStarted(
        { id, scope, occurrenceDate, repeat, ...patch },
        { dispatch, queryFulfilled },
      ) {
        let recipe: ((draft: ResidentTodoRecord[]) => void) | null = null;
        if (scope === undefined && repeat === undefined) {
          recipe = draft => {
            const todo = draft.find(t => t.id === id);
            // Only a one-off's patch is certain. One that lands on a repeat
            // (nothing sends one today) would diverge from what the server does
            // with it, so it waits.
            if (!todo || todo.repeat) return;
            Object.assign(todo, patch);
            // Client type uses `notes?: string`; a null clear maps to undefined.
            if (patch.notes === null) todo.notes = undefined;
          };
        } else if (
          scope === 'single' &&
          occurrenceDate &&
          patch.completed !== undefined &&
          Object.keys(patch).length === 1
        ) {
          const completed = patch.completed;
          recipe = draft => {
            const todo = draft.find(t => t.id === id);
            if (!todo) return;
            const at = todo.occurrences.findIndex(o => o.date === occurrenceDate);
            if (at >= 0) {
              todo.occurrences[at].completed = completed;
              // An un-tick can leave the record with nothing to say: drop it.
              if (isDefaultOccurrence(todo.occurrences[at])) todo.occurrences.splice(at, 1);
            } else if (completed) {
              // Every field explicit, so nothing is accidentally blanked. An
              // un-tick that finds no record has nothing to create.
              todo.occurrences.push({ ...DEFAULT_OCCURRENCE, date: occurrenceDate, completed });
            }
          };
        }
        if (!recipe) return;
        const patchResult = dispatch(
          residentTodosApi.util.updateQueryData('getResidentTodos', undefined, recipe),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ResidentTodo', id },
        { type: 'ResidentTodo', id: LIST_TAG_ID },
      ],
    }),

    // DELETE api/resident-todos/:id[?scope&occurrenceDate] — bare { success: true }.
    deleteResidentTodo: builder.mutation<void, DeleteResidentTodoInput>({
      query: ({ id, scope, occurrenceDate }) => ({
        url: `api/resident-todos/${id}`,
        method: 'DELETE',
        ...(scope ? { params: { scope, occurrenceDate } } : {}),
      }),
      invalidatesTags: [{ type: 'ResidentTodo', id: LIST_TAG_ID }],
    }),
  }),
});

export const {
  useGetResidentTodosQuery,
  useAddResidentTodoMutation,
  useUpdateResidentTodoMutation,
  useDeleteResidentTodoMutation,
} = residentTodosApi;
